import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { Cloud, Sun, Wind, Search, Plus, MessageCircle, LogOut, Thermometer, Droplets, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import './Dashboard.css';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [friends, setFriends] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsArticles, setNewsArticles] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadUserData();
    fetchNews();
  }, []);

  const fetchNews = async () => {
    setNewsLoading(true);
    // Helper to safely try a source
    const trySource = async (fn) => {
      try {
        const items = await fn();
        if (Array.isArray(items) && items.length > 0) {
          setNewsArticles(items.slice(0, 10));
          return true;
        }
      } catch (_) {
        // ignore per-source errors, move to next
      }
      return false;
    };

    // 1) Inshorts (community API) – may fail DNS or CORS
    const ok1 = await trySource(async () => {
      const res = await fetch('https://inshorts.deta.dev/news?category=all');
      if (!res.ok) return [];
      const json = await res.json();
      return (json?.data || []).map(d => ({
        title: d.title,
        imageUrl: d.imageUrl,
        content: d.content,
        author: d.author || 'NewsBytes',
        time: d.time || '',
        readMoreUrl: d.readMoreUrl
      }));
    });
    if (ok1) { setNewsLoading(false); return; }

    // 2) Spaceflight News (no key, CORS enabled and reliable)
    const ok2 = await trySource(async () => {
      const r3 = await fetch('https://api.spaceflightnewsapi.net/v4/articles/?limit=10');
      if (!r3.ok) return [];
      const j3 = await r3.json();
      return (j3?.results || []).map(a => ({
        title: a.title,
        imageUrl: a.image_url,
        content: a.summary,
        author: a.news_site || 'Newswire',
        time: new Date(a.published_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        readMoreUrl: a.url
      }));
    });
    if (ok2) { setNewsLoading(false); return; }

    // 3) GNews (requires key)
    const gKey = process.env.REACT_APP_GNEWS_API_KEY;
    if (gKey) {
      const ok3 = await trySource(async () => {
        const r2 = await fetch(`https://gnews.io/api/v4/top-headlines?category=general&lang=en&max=10&apikey=${gKey}`);
        if (!r2.ok) return [];
        const j2 = await r2.json();
        return (j2?.articles || []).map(a => ({
          title: a.title,
          imageUrl: a.image,
          content: a.description,
          author: a.source?.name || 'Newswire',
          time: new Date(a.publishedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          readMoreUrl: a.url
        }));
      });
      if (ok3) { setNewsLoading(false); return; }
    }

    toast.error('Unable to load headlines right now.');
    setNewsLoading(false);
  };

  // Derive deterministic meta for disguise: newspaper name, country and avatar color
  const getSourceMeta = (friendId, username) => {
    const seedStr = String(friendId || '') + String(username || '');
    let hash = 0;
    for (let i = 0; i < seedStr.length; i++) hash = (hash * 31 + seedStr.charCodeAt(i)) >>> 0;

    const papers = [
      'Northwind Times', 'Aurora Daily', 'Coastal Gazette', 'Skyline Chronicle', 'Harbor News Network',
      'Prairie Journal', 'Sierra Tribune', 'Metro Weather Watch', 'Frontier Bulletin', 'Seabreeze Herald'
    ];
    const countries = [
      { code: 'US', name: 'United States', emoji: '🇺🇸' },
      { code: 'GB', name: 'United Kingdom', emoji: '🇬🇧' },
      { code: 'CA', name: 'Canada', emoji: '🇨🇦' },
      { code: 'AU', name: 'Australia', emoji: '🇦🇺' },
      { code: 'IN', name: 'India', emoji: '🇮🇳' },
      { code: 'DE', name: 'Germany', emoji: '🇩🇪' },
      { code: 'FR', name: 'France', emoji: '🇫🇷' },
      { code: 'JP', name: 'Japan', emoji: '🇯🇵' },
      { code: 'BR', name: 'Brazil', emoji: '🇧🇷' },
      { code: 'ZA', name: 'South Africa', emoji: '🇿🇦' }
    ];
    const palette = ['#74b9ff', '#55efc4', '#81ecec', '#ffeaa7', '#fab1a0', '#a29bfe', '#fd79a8', '#00cec9'];

    const paper = papers[hash % papers.length];
    const country = countries[(hash >> 3) % countries.length];
    const avatarColor = palette[(hash >> 5) % palette.length];
    const initials = (username || 'U').slice(0, 2).toUpperCase();
    return { paper, country, avatarColor, initials };
  };

  const loadUserData = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (userDoc.exists()) {
        setUser(userDoc.data());
        await loadFriends(userDoc.data().friends || []);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadFriends = async (friendIds) => {
    try {
      const friendsData = [];
      for (const friendId of friendIds) {
        const friendDoc = await getDoc(doc(db, 'users', friendId));
        if (friendDoc.exists()) {
          friendsData.push({
            id: friendId,
            ...friendDoc.data()
          });
        }
      }
      setFriends(friendsData);
    } catch (error) {
      console.error('Error loading friends:', error);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('username', '>=', searchQuery), where('username', '<=', searchQuery + '\uf8ff'));
      const querySnapshot = await getDocs(q);
      
      const results = [];
      querySnapshot.forEach((doc) => {
        if (doc.id !== auth.currentUser.uid) {
          results.push({
            id: doc.id,
            ...doc.data()
          });
        }
      });
      
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addFriend = async (friendId) => {
    try {
      // Add to current user's friends list
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        friends: arrayUnion(friendId)
      });

      // Add current user to friend's friends list
      await updateDoc(doc(db, 'users', friendId), {
        friends: arrayUnion(auth.currentUser.uid)
      });

      toast.success('Friend added successfully!');
      loadUserData();
      setSearchResults([]);
      setSearchQuery('');
    } catch (error) {
      console.error('Error adding friend:', error);
      toast.error('Failed to add friend. Please try again.');
    }
  };

  const removeFriend = async (friendId) => {
    try {
      // Remove from current user's friends list
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        friends: arrayRemove(friendId)
      });

      // Remove current user from friend's friends list
      await updateDoc(doc(db, 'users', friendId), {
        friends: arrayRemove(auth.currentUser.uid)
      });

      toast.success('Friend removed successfully!');
      loadUserData();
    } catch (error) {
      console.error('Error removing friend:', error);
      toast.error('Failed to remove friend. Please try again.');
    }
  };

  const clearMyMessages = async () => {
    try {
      const confirmed = window.confirm('Clear all news threads for this account? This removes all your messages in Firebase.');
      if (!confirmed) return;
      const userId = auth.currentUser?.uid;
      if (!userId) return;
      const messagesRef = collection(db, 'messages');
      const q = query(messagesRef, where('participants', 'array-contains', userId));
      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        toast.success('No news to clear.');
        return;
      }
      let batch = writeBatch(db);
      let ops = 0;
      let deletedCount = 0;
      snapshot.forEach((docSnap) => {
        batch.delete(docSnap.ref);
        ops += 1;
        deletedCount += 1;
        if (ops >= 400) {
          batch.commit();
          batch = writeBatch(db);
          ops = 0;
        }
      });
      if (ops > 0) await batch.commit();
      toast.success(`Cleared ${deletedCount} news item${deletedCount !== 1 ? 's' : ''}.`);
    } catch (error) {
      console.error('Clear news error:', error);
      toast.error('Failed to clear news. Please try again.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const startChat = (friendId) => {
    navigate(`/chat/${friendId}`);
  };

  // Get current date formatted like a newspaper
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="dashboard-container">
      {/* Top Navigation Bar */}
      <div className="weather-header">
        <div className="weather-info">
          <div className="weather-main">
            <Sun className="weather-icon large" />
            <div className="temperature">
              <span className="temp-number">72°</span>
              <span className="temp-unit">F</span>
            </div>
          </div>
          <div className="weather-details">
            <div className="weather-detail">
              <Thermometer className="detail-icon" />
              <span>Feels like 75°</span>
            </div>
            <div className="weather-detail">
              <Droplets className="detail-icon" />
              <span>Humidity: 65%</span>
            </div>
            <div className="weather-detail">
              <Wind className="detail-icon" />
              <span>Wind: 8 mph</span>
            </div>
          </div>
        </div>
        
        <div className="user-section">
          <span className="username">{user?.username || 'Reader'}</span>
          <button onClick={handleLogout} className="logout-btn" title="Sign out">
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* Masthead */}
      <div className="news-masthead">
        <h1 className="masthead-title">The Daily Chronicle</h1>
        <p className="masthead-tagline">Your Trusted Source for Breaking News & Analysis</p>
        <p className="masthead-date">{currentDate}</p>
      </div>

      {/* Breaking News Ticker */}
      <div className="breaking-ticker">
        <span className="ticker-label">Breaking</span>
        <div className="ticker-content">
          <span className="ticker-text">
            Markets show steady growth amid global economic shifts • Weather patterns indicate changing seasonal trends • Technology sector leads innovation surge • Sports updates: Championship finals approach
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-content">
        <div className="content-section">
          {/* Search Section */}
          <div className="search-section">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search correspondents and news sources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
              />
              <button onClick={searchUsers} className="search-btn" disabled={loading}>
                <Search size={18} />
              </button>
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="search-results">
              <h3>Correspondents Found</h3>
              {searchResults.map((result) => (
                <div key={result.id} className="search-result">
                  <div className="result-info">
                    <span className="result-name">{result.username}</span>
                    <span className="result-email">{result.email}</span>
                  </div>
                  <button 
                    onClick={() => addFriend(result.id)}
                    className="add-btn"
                  >
                    <Plus size={14} />
                    Subscribe
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Main Grid: News + Correspondents Sidebar */}
          <div className="main-grid">
            {/* Headlines Section (real news for disguise) */}
            <div className="news-section">
              <h3>Top Stories</h3>
              {newsLoading ? (
                <div className="news-grid skeleton">
                  {Array.from({ length: 4 }).map((_, idx) => (
                    <div key={idx} className="news-card skeleton-card" />
                  ))}
                </div>
              ) : (
                <div className="news-grid">
                  {newsArticles.map((n, idx) => (
                    <article key={idx} className="news-card">
                      {n.imageUrl && (
                        <img src={n.imageUrl} alt="" className="news-image" loading="lazy" />
                      )}
                      <div className="news-content">
                        <div className="news-meta">
                          <span className="news-source">{n.author || 'Newswire'}</span>
                          <span className="news-time">{n.time || ''}</span>
                        </div>
                        <h4 className="news-title">{n.title}</h4>
                        {n.content && <p className="news-body">{n.content}</p>}
                        {n.readMoreUrl && (
                          <a href={n.readMoreUrl} target="_blank" rel="noreferrer" className="news-link">Continue Reading →</a>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>

            {/* Correspondents/Sources Sidebar */}
            <aside className="friends-section">
              <div className="friends-header">
                <h3>My Sources</h3>
                <button className="clear-btn" onClick={clearMyMessages} title="Clear archives">Clear</button>
              </div>
              {friends.length === 0 ? (
                <p className="no-friends">No sources yet. Search above to add trusted correspondents.</p>
              ) : (
                <div className="friends-list">
                  {friends.map((friend) => {
                    const meta = getSourceMeta(friend.id, friend.username);
                    return (
                      <div 
                        key={friend.id} 
                        className="friend-item"
                        onClick={() => startChat(friend.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && startChat(friend.id)}
                        title={`Read updates from ${friend.username}`}
                      >
                        <div className="friend-info">
                          <div className="friend-avatar source-avatar" style={{ background: meta.avatarColor }}>
                            <span className="source-initials">{meta.initials}</span>
                          </div>
                          <div className="friend-details">
                            <span className="friend-name">{friend.username}</span>
                            <div className="friend-status">
                              <span className="paper-badge">{meta?.paper || 'Independent'}</span>
                              <span className="country-pill">{meta?.country?.emoji} {meta?.country?.name}</span>
                            </div>
                          </div>
                        </div>
                        <div className="friend-actions">
                          <button 
                            onClick={(e) => { e.stopPropagation(); startChat(friend.id); }}
                            className="chat-btn"
                            aria-label={`Read from ${friend.username}`}
                          >
                            <Eye size={14} />
                            Read
                          </button>
                          <button 
                            onClick={(e) => { e.stopPropagation(); removeFriend(friend.id); }}
                            className="remove-btn"
                            aria-label={`Unsubscribe from ${friend.username}`}
                          >
                            Unsub
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
